import { Chip, cn, Modal, ModalBody, ModalContent, ModalHeader } from '@ds';
import { format } from 'date-fns';
import { Fragment } from 'react/jsx-runtime';
import { ScheduleCalendarGrid } from '@lp/features/Scheduler/components';
import { CSResourcesWithShifts } from '@/api/apis/resources/schedule';
import { usePhoneSupportDayTotals } from './hooks';
import { OrganType, OrganTypeWithoutKidney } from '@/api/apis/case';
import { getColorsByOrganType, ORGAN_ICON } from '@/utils/helpers';

type PhoneSupportModalProps = {
  daysInterval: Date[];
  shiftsData: CSResourcesWithShifts;
  startDay: Date;
  selectedDate: Date;
  isOpen: boolean;
  onClose: () => void;
  /** Window label, e.g. "Period 9 · 2026" or "August 2026". */
  periodLabel: string;
  /** Secondary date-range line, e.g. "13 August – 9 September". Only shown in period mode. */
  periodDateRange: string | null;
};

export const PhoneSupportModal = ({
  daysInterval,
  shiftsData,
  startDay,
  selectedDate,
  isOpen,
  onClose,
  periodLabel,
  periodDateRange,
}: PhoneSupportModalProps) => {
  const totals = usePhoneSupportDayTotals(daysInterval, startDay, shiftsData);

  return (
    <Modal
      classNames={{
        wrapper: 'flex-wrap',
        base: 'p-6 bg-base-80 m-2!',
        header: 'text-hl! font-secondary p-0 pb-4 border-b-1 border-base-30',
        body: 'p-0 my-6',
        footer: 'p-0 pt-6 border-t-1 border-base-30 justify-between',
        closeButton: 'text-base-20 m-4 text-hl',
      }}
      size="5xl"
      backdrop="opaque"
      isOpen={isOpen}
      onClose={onClose}
    >
      <ModalContent>
        <>
          <ModalHeader>
            <div className="flex flex-col">
              <span className="text-sm font-secondary text-base-25">Clinical Lead</span>
              <h3>{periodLabel}</h3>
              {periodDateRange && <span className="text-hxs font-secondary text-base-15">{periodDateRange}</span>}
            </div>
          </ModalHeader>
          <ModalBody className="gap-0">
            <ScheduleCalendarGrid>
              {totals.map((dayTotal) => {
                const isSelectedDay = selectedDate.toISOString() === dayTotal.day.toISOString();
                return (
                  <div
                    key={dayTotal.day.toISOString()}
                    className={`h-[120px] border px-2 py-2 pt-1 text-right text-sm font-semibold ${!dayTotal.isCurrentMonth ? 'text-base-30' : 'text-base-20'} ${isSelectedDay ? 'bg-info-80' : ''}`}
                  >
                    <span>{format(dayTotal.day, 'd MMM')}</span>
                    {dayTotal.isCurrentMonth &&
                      // TMDXLP-14091: Kidneys hidden from Clinical Lead modal. Revert OrganTypeWithoutKidney -> OrganType to restore.
                      Object.keys(OrganTypeWithoutKidney).map((organ) => {
                        const OrganIcon = ORGAN_ICON[organ as OrganType] ?? Fragment;
                        return (
                          <Chip
                            size="sm"
                            className={cn('mt-1 min-w-full px-2 bg-base-60', {
                              [getColorsByOrganType(organ as OrganType).bg40]: isSelectedDay,
                            })}
                            key={organ}
                            startContent={
                              <div className="flex items-center gap-1">
                                <OrganIcon key={organ} className=" size-4" /> {organ}
                              </div>
                            }
                            endContent={<span>{dayTotal[organ as OrganType] || 0}</span>}
                          />
                        );
                      })}
                  </div>
                );
              })}
            </ScheduleCalendarGrid>
          </ModalBody>
        </>
      </ModalContent>
    </Modal>
  );
};
